
import { Investor, Contact } from "@/types/investor";
import { getAPIKey } from "./apiKeyUtils";
import { aiDebugState } from "./debugUtils";

/**
 * Generate email content using OpenAI
 */
export async function generateEmailContent(
  investor: Investor,
  contact: Contact,
  businessInfo: string,
  apiKey: string
): Promise<string> {
  try {
    const investorName = investor.name || contact.name || "Unknown";
    
    const prompt = `
      You are a professional email writer crafting a forwardable introduction email for a startup founder.
      
      CONTEXT:
      The email should be formatted as a simple request to a Techstars team member, who will then forward it to the investor.
      This is NOT a direct email to the investor, but rather to the Techstars team member asking for an introduction.
      
      STARTUP INFORMATION:
      ${businessInfo}
      
      INVESTOR INFORMATION:
      Name: ${investorName}
      Firm: ${investor.firm}
      Investment Focus: ${investor.investmentFocus || "Not available"}
      Portfolio Companies: ${investor.portfolioCompanies?.join(", ") || "Not available"}
      Keywords/Interests: ${investor.keywords?.join(", ") || "Not available"}
      Relevance Score: ${investor.relevanceScore}/10
      
      CONTACT INFORMATION:
      Name: ${contact.name}
      Role: ${contact.role}
      
      INSTRUCTIONS:
      1. Create a concise, professional email addressed to a Techstars team member (use "[Techstars Contact]")
      2. The email should request an introduction to the investor
      3. EMPHASIZE THE COMPANY'S TRACTION METRICS (revenue, growth rates, customer count) - this is extremely important
      4. Highlight any financial metrics, especially current revenue and pipeline values
      5. Explain why this specific investor is a good fit based on their portfolio, focus areas, and investment history
      6. Reference 1-2 specific portfolio companies if they are relevant to your startup
      7. Keep the entire email concise and scannable (maximum 200 words)
      8. The tone should be professional, researched, and confident
      9. Include a simple call to action asking if the Techstars contact would make the introduction
      10. Format with proper greeting, body paragraphs, and sign-off using "[Your Name]"
      
      DO NOT include any "forwarded part" sections or dividers like "---".
      
      Output just the email text, properly formatted, without any additional notes or explanations.
    `;

    // Save the prompt for debugging
    aiDebugState.lastPrompt = prompt;
    aiDebugState.timestamp = new Date().toISOString();
    const startTime = Date.now();

    // Always use OpenAI for email generation
    const openaiKey = getAPIKey('openai', apiKey);
    
    if (!openaiKey) {
      throw new Error("OpenAI API key is required for email generation");
    }

    const response = await fetch("https://api.openai.com/v1/chat/completions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${openaiKey}`
      },
      body: JSON.stringify({
        model: "gpt-4o", 
        messages: [
          {
            role: "system",
            content: "You are an expert at writing forwardable introduction emails for startup founders seeking warm introductions to investors through accelerator networks."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        temperature: 0.7,
        max_tokens: 1000,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error?.message || "Failed to generate email content");
    }

    const data = await response.json();
    const content = data.choices[0].message.content;
    
    // Save the response and timing for debugging
    aiDebugState.lastResponse = content;
    aiDebugState.responseTime = Date.now() - startTime;
    aiDebugState.model = data.model || "gpt-4o";
    
    return content;
  } catch (error) {
    console.error("Error in generateEmailContent:", error);
    throw error;
  }
}
